import { defaultColumnConfigurations } from '../store/orders/grid/default-column-configuration';
import { LogService } from './log-service';

// The implementation will change once we move to a new user grid config table
const statusIdStringIndex = 3;
const filterViewIdStringIndex = 4;

const v2ToV3ColumnIdMap = {
  Age: 'age',
  AllocationStatus: 'allocation-status',
  AmountPaid: 'amount-paid',
  AssignedUser: 'assignedTo',
  BillName: 'buyer',
  Username: 'buyer-id',
  CarrierID: 'carrier',
  ShipCompany: 'company',
  Confirmation: 'confirmation',
  ShipCountryCode: 'country',
  CustomField1: 'custom1',
  CustomField2: 'custom2',
  CustomField3: 'custom3',
  HoldUntil: 'holdUntil',
  ItemName: 'item-name',
  ItemSku: 'item-sku',
  Notes: 'notes',
  BatchNumber: 'open-batch-name',
  OrderNumber: 'order-number',
  OrderDate: 'orderDate',
  OrderTotal: 'order-total',
  RequestedPackageTypeID: 'package',
  PayDate: 'paidDate',
  Quantity: 'quantity',
  Rate: 'rate',
  ShipName: 'recipient',
  RequestedShippingService: 'requested-service',
  ServiceID: 'service',
  ShipByDate: 'ship-by',
  ShipDate: 'shipDate',
  WarehouseID: 'shipFrom',
  BillTo: 'shippingAccount',
  ShippingAmount: 'shippingPaid',
  Source: 'source',
  ShipState: 'state',
  OrderStatusID: 'status',
  StoreName: 'store',
  Tags: 'tags',
  TaxAmount: 'taxPaid',
  WarehouseLocation: 'warehouse-location',
  TotalOz: 'weight',
  Zone: 'zone',
};

const v2ToV3StatusViewIdMap = {
  1: 'awaiting-payment',
  2: 'awaiting-shipment',
  3: 'shipped',
  4: 'cancelled',
  5: 'on-hold',
  7: 'pending-fulfillment',
  SearchResults: 'advanced-search',
  Batch: 'batches',
};

const buildV3ColumnFromV2 = (v2Config) => {
  const v2OrderById = v2Config.sort;
  const v2OrderByDir = v2Config.dir;
  const v3OrderByID = v2ToV3ColumnIdMap[v2OrderById];
  const v3OrderByDir = v2OrderByDir === 'asc' ? 'ascending' : 'descending';
  const v2Columns = v2Config.columns;

  return v2Columns
    .filter((c) => {
      const v2ColId = typeof c === 'string' ? c : c.col;
      return !!v2ToV3ColumnIdMap[v2ColId];
    })
    .map((v2Col, index) => {
      const v2ColId = typeof v2Col === 'string' ? v2Col : v2Col.col;
      const v2ColWidth = typeof v2Col === 'string' ? undefined : v2Col.wid;
      const v3ColumnId = v2ToV3ColumnIdMap[v2ColId];
      const v3ColumnDefault = defaultColumnConfigurations.find((c) => c.id === v3ColumnId) || {};
      const width = v2ColWidth || v3ColumnDefault.width;
      return {
        id: v3ColumnId,
        ...v3ColumnDefault,
        width: typeof width === 'string' ? parseInt(width.replace(/\D+/g, ''), 10) : width,
        sorted: v3ColumnId === v3OrderByID ? v3OrderByDir : undefined,
        index,
      };
    });
};

const versionConfigsToShow = (v2OrdersGridConfigs, existingV3ConfigMap, stores, legacyColumnsAllowed) => {
  const configsToUseInV3 = {};
  const existingV3ConfigKeys = Object.keys(existingV3ConfigMap);
  const hasExistingV3Configs = !!existingV3ConfigKeys.length;
  const hasV2Configs = !!v2OrdersGridConfigs.length;

  if (!hasV2Configs && !hasExistingV3Configs) {
    return configsToUseInV3;
  }

  if (hasExistingV3Configs && !hasV2Configs) {
    return existingV3ConfigMap;
  }

  if (!legacyColumnsAllowed && hasV2Configs && hasExistingV3Configs) {
    return existingV3ConfigMap;
  }

  if (legacyColumnsAllowed && hasV2Configs) {
    v2OrdersGridConfigs.forEach((v2Setting) => {
      const splitConfigName = v2Setting.settingName.split(':');

      // TODO: port filter view configs
      // v2 config names are in the pattern - Orders:View:1:23 where the 4th value is a filter view id
      if (splitConfigName.length === 4) {
        return;
      }

      const v2StatusId = splitConfigName[2];
      const v3StatusId = v2ToV3StatusViewIdMap[v2StatusId];
      const v2Config = JSON.parse(v2Setting.settingValue);
      const storeConfigs = [];
      if (stores.length) {
        stores.forEach((store) => {
          const buildStoreViewName = `${v3StatusId}-${store.storeGuid}`;
          storeConfigs.push(buildStoreViewName);
          const v3Columns = buildV3ColumnFromV2(v2Config);
          configsToUseInV3[buildStoreViewName] = {
            default: v3Columns,
          };
        });
      }

      if (v2StatusId === 'SearchResults') {
        const allOrdersSearchResult = 'all-orders-search-result';
        const v3Columns = buildV3ColumnFromV2(v2Config);
        existingV3ConfigKeys
          .filter((key) => key.includes(allOrdersSearchResult))
          .forEach((config) => {
            configsToUseInV3[config] = {
              default: existingV3ConfigMap[config]?.default || v3Columns,
            };
          });
      }

      const hasV3ConfigForSetting = existingV3ConfigKeys.filter((key) => key.includes(v3StatusId));
      if (hasV3ConfigForSetting.length) {
        existingV3ConfigKeys
          .filter((key) => key.includes(v3StatusId))
          .forEach((status, idx) => {
            if (status === storeConfigs[idx]) {
              configsToUseInV3[status] = {
                default: existingV3ConfigMap[status].default,
              };
            }
            if (status === v3StatusId) {
              configsToUseInV3[status] = {
                default: existingV3ConfigMap[status].default,
              };
            } else {
              const v3Columns = buildV3ColumnFromV2(v2Config);
              configsToUseInV3[v3StatusId] = {
                default: v3Columns,
              };
            }
          });
        return;
      }

      const v3Columns = buildV3ColumnFromV2(v2Config);
      configsToUseInV3[v3StatusId] = {
        default: v3Columns,
      };
    });
    // this is to add any remaining v3 configs that dont have corresponding v2 configs
    existingV3ConfigKeys.forEach((key) => {
      configsToUseInV3[key] = existingV3ConfigMap[key];
    });

    return configsToUseInV3;
  }
};

const attemptSettingValueParse = (settingValue) => {
  try {
    return JSON.parse(settingValue);
  } catch (e) {
    LogService.logError(e, { settingValue });
  }
};

class GridConfigUserSettingServiceClass {
  buildUserGridConfigMap(storedUserConfigsSettings) {
    const userGridConfigsMap = storedUserConfigsSettings.reduce((gridConfigMap, configSetting) => {
      const { settingName, settingValue } = configSetting;
      const splitSettingName = settingName.split('.');
      // TODO - some accounts might have the the entire grid's config setting instead of the 1 view to 1 config setting
      if (splitSettingName.length <= 4) {
        return gridConfigMap;
      }
      const statusViewId = splitSettingName[statusIdStringIndex];
      const filterViewId = splitSettingName[filterViewIdStringIndex];

      return {
        ...gridConfigMap,
        [statusViewId]: {
          ...gridConfigMap[statusViewId],
          [filterViewId]: attemptSettingValueParse(settingValue),
        },
      };
    }, {});

    return userGridConfigsMap;
  }

  AllowUseOfLegacyColumns = 'AllowUseOfLegacyColumns';

  mergeV2AndV3OrdersGridConfigs = (userSettings, stores) => {
    const v2OrdersGridConfigs = userSettings.filter((setting) => setting.settingName.includes('Orders:View') && setting.settingValue);
    const v3GridConfigSettings = userSettings.filter((setting) => setting.settingName.includes('v3.orders.gridConfig'));
    const legacyColumnsAllowed =
      userSettings.find((setting) => setting.settingName === this.AllowUseOfLegacyColumns)?.settingValue === 'true';

    const existingV3ConfigMap = this.buildUserGridConfigMap(v3GridConfigSettings);
    return versionConfigsToShow(v2OrdersGridConfigs, existingV3ConfigMap, stores, legacyColumnsAllowed);
  };
}

export const GridConfigUserSettingService = new GridConfigUserSettingServiceClass();
